import { urlToFile } from "./urlToFile";
import {
  fetchCreationImageBlob,
  fetchUploadImageBlob,
} from "../services/api/editor/background_removal";

/**
 * Hosts the BFF will fetch server-side. Keep in step with
 * BgRemoveBackend services/photostudio_features_service.py.
 */
export function isAllowlistedImageHost(url) {
  try {
    const host = new URL(url).hostname.toLowerCase();
    if (host.includes("amazonaws.com")) return true;
    if (host.endsWith(".cloudfront.net")) return true;
    if (host.endsWith(".cloudinary.com")) return true;
    if (host.endsWith(".r2.cloudflarestorage.com")) return true;
    if (host === "localhost" || host === "127.0.0.1") return true;
    return false;
  } catch {
    return false;
  }
}

function positiveId(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function creationIdFromAppUrl(url) {
  const match = String(url).match(/\/creations\/(\d+)\/(?:image|input-image)/);
  return match ? Number(match[1]) : null;
}

async function fileFromAppImage({
  token,
  creationId,
  rootCreationId,
  filename,
}) {
  if (!token) return null;
  const cid = positiveId(creationId);
  const rootId = positiveId(rootCreationId);

  if (cid) {
    const blob = await fetchCreationImageBlob(token, cid);
    if (blob && !String(blob.type || "").includes("json")) {
      return new File([blob], filename, { type: blob.type || "image/png" });
    }
  }

  if (rootId && rootId !== cid) {
    const blob = await fetchUploadImageBlob(token, rootId);
    if (blob && !String(blob.type || "").includes("json")) {
      return new File([blob], filename, { type: blob.type || "image/png" });
    }
  }

  return null;
}

/**
 * Resolve a prep/editor image into BFF `file` xor `image_url`.
 * Prefers an existing File. Blob/data URLs are uploaded as a File.
 * http(s) URLs on allowlisted hosts are passed through. App creation or
 * upload images on other hosts are loaded same-origin and sent as a File.
 *
 * @param {object} [opts]
 * @param {string|null} [opts.sourceUrl]
 * @param {File|null} [opts.resolvedFile]
 * @param {string} [opts.filename]
 * @param {string} [opts.missingError]
 * @param {string} [opts.unsupportedError]
 * @param {string|null} [opts.token]
 * @param {number|string|null} [opts.creationId]
 * @param {number|string|null} [opts.rootCreationId]
 * @returns {Promise<{ file: File|null, image_url: string|null }>}
 */
export async function resolveImageSource({
  sourceUrl = null,
  resolvedFile = null,
  filename = "input.png",
  missingError = "No image loaded. Please upload an image and try again.",
  unsupportedError = "Unsupported image URL for this operation.",
  token = null,
  creationId = null,
  rootCreationId = null,
} = {}) {
  if (resolvedFile instanceof File) {
    return { file: resolvedFile, image_url: null };
  }

  if (typeof sourceUrl === "string" && sourceUrl) {
    if (sourceUrl.startsWith("blob:") || sourceUrl.startsWith("data:")) {
      const file = await urlToFile(sourceUrl, filename);
      return { file, image_url: null };
    }
    if (/^https?:\/\//i.test(sourceUrl.trim())) {
      const url = sourceUrl.trim();
      const needsFile = !isAllowlistedImageHost(url);
      const cid = positiveId(creationId) || creationIdFromAppUrl(url);
      const rootId = positiveId(rootCreationId);
      if (needsFile && token && (cid || rootId)) {
        const file = await fileFromAppImage({
          token,
          creationId: cid,
          rootCreationId: rootId,
          filename,
        });
        if (file) return { file, image_url: null };
      }
      return { file: null, image_url: url };
    }
    throw new Error(unsupportedError);
  }

  throw new Error(missingError);
}
